/* eslint-disable max-len */
/* eslint no-nested-ternary: 0 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import { Trans, withTranslation } from 'react-i18next';
import {
  Feed,
  Grid,
  List,
} from 'semantic-ui-react';
import _ from 'lodash';
import moment from 'moment-timezone';
import PROJECT_STATUSES from '../constants/projectStatuses';
import { bookingNoteDefaultPrivacyOptions } from '../constants/bookingNote';
import { bookingNoteAccessesOptions, paymentReceiptAccessesOptions, rduAccessesOptions } from '../constants/srRoleAccess';
import { namespaces, TRANSLATION_FILES } from '../constants/translation';
import { getConstantsLabel, translateModels } from '../utils/translation';
import { camelToTitle } from '../utils/strings';
import { parseJSON } from '../utils/json';
import Timestamp from './Timestamp';

// Still needed for the module-level locale; the feed date renders through Timestamp.
moment.locale('en-gb');

class ProjectActivities extends Component {
  static propTypes = {
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    hasAccess: PropTypes.bool.isRequired,
    loading: PropTypes.bool.isRequired,
    showAll: PropTypes.bool,
    addressSettings: PropTypes.shape({
      data: PropTypes.array,
    }),
    t: PropTypes.func,
  }
  getAddressLabel = (key) => {
    const { addressSettings } = this.props;
    if (addressSettings && addressSettings.data) {
      const as = addressSettings.data.find(a => _.camelCase(a.columnName) === key);
      if (as) {
        return as.label;
      }
    }
    return key;
  }
  keyToName = (key) => {
    const { t } = this.props;
    switch (key) {
      case 'address1':
      case 'address2':
      case 'address3':
      case 'address4':
      case 'address5':
      case 'address6':
      case 'address7':
      case 'address8':
      case 'address9':
      case 'address10':
        return this.getAddressLabel(key);
      default:
        return translateModels(t, key, 'project', camelToTitle(key));
    }
  }
  renderActivityMessage = (activity) => {
    const { t } = this.props;
    const activityBefore = parseJSON(activity.before);
    const activityAfter = parseJSON(activity.after);
    const afterKeys = Object.keys(activityAfter);
    const changes = [];
    afterKeys.forEach((element) => {
      let message;
      let after = '';
      let before = activityBefore[element];
      let mpPublishState = 'removed from';
      if (activity.before) {
        if (!_.isEmpty(activityAfter[element]) || _.isNumber(activityAfter[element]) || _.isBoolean(activityAfter[element])) {
          after = activityAfter[element];
        }
      }
      switch (element) {
        case 'marketplaceIsPublish':
        case 'marketplaceBuyerIsPublish':
          if (before === false && after) {
            mpPublishState = 'launched to';
          }
          message = <span>{t(`Project is ${mpPublishState}`)} <span style={{ color: '#666' }}>{this.keyToName(element)}</span></span>;
          break;
        case 'statusID':
          before = getConstantsLabel(t, PROJECT_STATUSES, before);
          after = getConstantsLabel(t, PROJECT_STATUSES, after);
          break;
        case 'address1':
        case 'address2':
          if (!before) message = <span><span style={{ color: '#666' }}>{this.keyToName(element)}</span>: <u>{after}</u></span>;
          break;
        case 'bnDefaultPrivacy':
          before = getConstantsLabel(t, bookingNoteDefaultPrivacyOptions, before);
          after = getConstantsLabel(t, bookingNoteDefaultPrivacyOptions, after);
          break;
        case 'bnReadAccessGroupIDs':
        case 'bnWriteAccessGroupIDs':
          before = getConstantsLabel(t, bookingNoteAccessesOptions, before);
          after = getConstantsLabel(t, bookingNoteAccessesOptions, after);
          break;
        case 'rduAccessGroupIDs':
          before = getConstantsLabel(t, rduAccessesOptions, before);
          after = getConstantsLabel(t, rduAccessesOptions, after);
          break;
        case 'paymentReceiptAccessGroupIDs':
          before = getConstantsLabel(t, paymentReceiptAccessesOptions, before);
          after = getConstantsLabel(t, paymentReceiptAccessesOptions, after);
          break;
        default:
          break;
      }
      if (message) {
        changes.push(message);
      } else if (before || after) {
        const keyName = this.keyToName(element);
        if (before === false && after) {
          let text = t('has been added');
          if (_.isBoolean(after)) text = t('is enabled');
          message = (
            <Trans ns={TRANSLATION_FILES.screens}>
              <span><span style={{ color: '#666' }}>{{ keyName }}</span> {text}</span>
            </Trans>
          );
        } else if (!before) {
          message = <span><span style={{ color: '#666' }}>{keyName}</span>: <u>{after}</u></span>;
        } else if (before && !after) {
          let text = t('has been removed');
          if (_.isBoolean(before)) text = t('is disabled');
          message = (
            <Trans ns={TRANSLATION_FILES.screens}>
              <span><span style={{ color: '#666' }}>{{ keyName }}</span> {text}</span>
            </Trans>
          );
        } else {
          message = (
            <Trans ns={TRANSLATION_FILES.screens}>
              <span><span style={{ color: '#666' }}>{{ keyName }}</span> changed from <u>{{ before }}</u> to <u>{{ after }}</u></span>
            </Trans>
          );
        }
        changes.push(message);
      }
    });
    return (
      <List bulleted>
        {changes.map((element, id) => (
          <List.Item key={id}>
            {element}
          </List.Item>
        ))}
      </List>
    );
  };
  renderActivityTitle = (title) => {
    const { t } = this.props;
    let loweredTitle = title.toLowerCase();

    const copyKeyword = 'Copied from';
    if (title.includes(copyKeyword)) {
      const projectName = title.slice(title.indexOf(copyKeyword) + copyKeyword.length);
      title = title.replace(projectName, '{{project}}');
      return t(title, { project: projectName });
    }
    const packageKeyword = 'package (';
    if (loweredTitle.includes(packageKeyword)) {
      let formulaName = '';
      const formulaKeyword = 'formula (';
      if (loweredTitle.includes(formulaKeyword)) {
        formulaName = title.substring(loweredTitle.indexOf(formulaKeyword) + formulaKeyword.length);
        formulaName = `(${formulaName.substring(0, formulaName.indexOf(')') + 1)}`;
        title = title.replace(formulaName, '{{formula}}');
        loweredTitle = title.toLowerCase();
      }

      let packageName = title.slice(loweredTitle.indexOf(packageKeyword) + packageKeyword.length);
      packageName = `(${packageName}`;
      title = title.replace(packageName, '{{package}}');
      return t(title, { formula: formulaName, package: packageName });
    }
    return t(title);
  }
  render() {
    const { data, t } = this.props;
    return (
      <Grid>
        <Grid.Row>
          <Grid.Column width={16}>
            <Feed>
              { data && data.length > 0 ?
              data.map((activity, idx) => (
                <Feed.Event key={idx} className="activity-logs">
                  <Feed.Label icon="user" />
                  <Feed.Content>
                    <Feed.Date>
                      <Timestamp timestamp={activity.createdAt} format="MMMM Do YYYY, h:mm:ss a" />
                    </Feed.Date>
                    <Feed.Summary>
                      {this.props.hasAccess ?
                        activity.maUserAffiliateID > 0 ?
                          <Link to={`/users/${activity.maUserAffiliateID}/`}>
                            {activity.maCreatedByPreferredName || t('Unknown')}
                          </Link>
                           : ''
                         : activity.maCreatedByPreferredName
                      }
                      {` ${this.renderActivityTitle(activity.message)}`}
                      { this.props.showAll ?
                        <span>
                          &nbsp;
                          <Link to={`/projects/${activity.projectID}/`}>
                            {activity.projectName}
                          </Link>
                        </span>
                        : ''
                      }
                    </Feed.Summary>
                    <Feed.Extra text>
                      { activity.after && this.renderActivityMessage(activity) }
                    </Feed.Extra>
                  </Feed.Content>
                </Feed.Event>
              )) :
                this.props.loading ?
                  <br />
                : <center>{t('No activity found')}.</center>
              }
            </Feed>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }
}

export default withTranslation([...namespaces, TRANSLATION_FILES.models.project])(connect(ProjectActivities.mapStateToProps)(ProjectActivities));



// WEBPACK FOOTER //
// ./src/components/ProjectActivities.jsx